/**
 * The project's Team Work sessions (Work, Team Work): every session its members' workbenches keep at the project's
 * home, newest first; each opens the session view, where people read it, reply, steer and answer.
 */

import { Comments } from '@gravity-ui/icons';

import { routes } from '../../api/client.ts';
import { timeAgo, useLoad } from '../../hooks.ts';
import { Empty, ErrorNote, ListBlock, ListGroup, ListRow } from '../ui.tsx';
import { VIEW_TONES } from '../ui/Art.tsx';

export function TeamworkList({ projectId }: { projectId: string }) {
  const tone = VIEW_TONES.projects!;
  const list = useLoad(() => routes.teamworkConversations(projectId), [projectId], 30_000);
  const teams = useLoad(() => routes.projectTeamsV2(projectId).catch(() => []), [projectId]);
  const teamName = (id?: string) => (id ? teams.data?.find((t) => t.id === id)?.name : undefined);
  const rows = (list.data?.conversations ?? []).filter((c) => !c.data.archived);
  return (
    <ListGroup
      title="Team Work"
      count={rows.length || undefined}
      description="Sessions where a team works with the project's people; open one to read it, reply or answer a question."
      empty={list.data && !rows.length ? <Empty compact tone={tone} icon={<Comments />} title="No Team Work sessions yet" body="A session starts in AI Workbench when a member gives a team some work; it shows here for everyone on the project." /> : undefined}
    >
      {list.error ? (
        <ListBlock>
          <ErrorNote error={list.error} />
        </ListBlock>
      ) : null}
      {!list.data && !list.error ? <ListBlock className="text-sm text-muted">Reading the sessions…</ListBlock> : null}
      {rows.map((c) => (
        <ListRow
          key={c.id}
          title={c.data.title || 'Untitled session'}
          description={[teamName(c.data.teamId), c.data.owner ? `started by ${c.data.owner.name}` : undefined, `updated ${timeAgo(c.updatedAt)}`].filter(Boolean).join(' · ')}
          to={`/projects/${encodeURIComponent(projectId)}/teamwork/${encodeURIComponent(c.id)}`}
        />
      ))}
    </ListGroup>
  );
}
